import {
  Add,
  ArrowDownward,
  ArrowUpward,
  DeleteOutlined,
  ErrorOutline,
} from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControlLabel,
  Grid2,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { ControlledSelectField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-select-field";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  EntityRef,
  ExtendedFieldError,
  FormBlock,
  FormQuestion,
  getEntityRefTarget,
} from "vmd-types";

import { AntSwitch } from "../../components/ant-switch";
import { BlockQuestionList } from "./block-question-list";
import { DraftTextField } from "./draft-text-field";
import { ConditionCandidate } from "./form-question-editor";
import { isSelectAnswerType, t } from "./labels";
import { BLOCK_TRANSITION_MS, withOrder } from "./utils";

export interface FormBlockEditorProps {
  block: FormBlock;
  formIndex: number;
  blockIndex: number;
  isFirst: boolean;
  isLast: boolean;
  expanded: boolean;
  onToggleExpanded: (expanded: boolean) => void;
  /** Every question in the blueprint, for cross-question "conditional on". */
  allQuestions: ConditionCandidate[];
  validationErrors: Record<string, ExtendedFieldError>;
  language: string;
  onChange: (block: FormBlock) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

export const FormBlockEditor: FC<FormBlockEditorProps> = ({
  block,
  formIndex,
  blockIndex,
  isFirst,
  isLast,
  expanded,
  onToggleExpanded,
  allQuestions,
  validationErrors,
  language,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
}) => {
  const titleKey = `trackingItemForms.${formIndex}.blocks.${blockIndex}.title`;
  const codeKey = `trackingItemForms.${formIndex}.blocks.${blockIndex}.code`;
  const questions = block.questions ?? [];

  // A block cannot meaningfully depend on one of its own questions: if hidden,
  // that question could never be answered in the first place.
  const ownQuestionIds = useMemo(
    () => new Set(block.questions?.map((q) => q.id)),
    [block.questions]
  );
  const conditionOptions = useMemo(
    () => [
      { value: "", label: t(language, "formBlocks.noCondition", "None") },
      ...allQuestions
        .filter((c) => !ownQuestionIds.has(c.id))
        .map((c) => ({ value: c.id, label: c.label })),
    ],
    [allQuestions, ownQuestionIds, language]
  );

  const controllingQuestion = allQuestions.find(
    (c) => c.id === block.conditionalOn?.questionId
  );
  const controllingIsSelect = controllingQuestion
    ? isSelectAnswerType(controllingQuestion.answerType)
    : false;
  const controllingEnumValues: readonly string[] =
    controllingQuestion?.entityRef
      ? (getEntityRefTarget(controllingQuestion.entityRef as EntityRef)
          .values ?? [])
      : [];
  const controllingHasOptions =
    controllingIsSelect || controllingEnumValues.length > 0;
  const equalsValues = block.conditionalOn?.equals ?? [];

  const setControllingQuestion = (questionId: string) =>
    onChange({
      ...block,
      conditionalOn: questionId ? { questionId } : undefined,
    });

  const setEquals = (values: string[]) => {
    if (!block.conditionalOn) return;
    onChange({
      ...block,
      conditionalOn: {
        questionId: block.conditionalOn.questionId,
        equals: values,
      },
    });
  };

  // Validation errors anywhere under this block, surfaced on the header so a
  // collapsed block still flags that something inside needs attention.
  const blockErrorPrefix = `trackingItemForms.${formIndex}.blocks.${blockIndex}.`;
  const blockErrorKeys = Object.keys(validationErrors ?? {}).filter((key) =>
    key.startsWith(blockErrorPrefix)
  );
  const erroringQuestionCount = new Set(
    blockErrorKeys
      .map((key) => key.match(/\.questions\.(\d+)\./)?.[1])
      .filter((index): index is string => index !== undefined)
  ).size;
  const hasBlockErrors = blockErrorKeys.length > 0;

  const setQuestions = (next: FormQuestion[]) =>
    onChange({ ...block, questions: withOrder(next) });

  const addQuestion = () => {
    const question: FormQuestion = {
      id: uuidv4(),
      fieldName: "",
      label: "",
      answerType: "text",
      order: questions.length,
      enabled: true,
    };
    setQuestions([...questions, question]);
  };

  // A block can hold ~60 questions, so the per-question handlers must keep a
  // stable identity or FormQuestionEditor's memo never hits and one keystroke
  // re-renders every editor. The ref supplies the current block instead.
  const latest = useRef({ block, onChange });
  useEffect(() => {
    latest.current = { block, onChange };
  }, [block, onChange]);

  const commitQuestions = useCallback(
    (mutate: (questions: FormQuestion[]) => FormQuestion[] | undefined) => {
      const current = latest.current;
      const next = mutate([...(current.block.questions ?? [])]);
      if (!next) return;
      current.onChange({ ...current.block, questions: withOrder(next) });
    },
    []
  );

  const updateQuestion = useCallback(
    (index: number, question: FormQuestion) =>
      commitQuestions((next) => {
        next[index] = question;
        return next;
      }),
    [commitQuestions]
  );

  const deleteQuestion = useCallback(
    (index: number) =>
      commitQuestions((next) => {
        next.splice(index, 1);
        return next;
      }),
    [commitQuestions]
  );

  const moveQuestion = useCallback(
    (index: number, direction: -1 | 1) =>
      commitQuestions((next) => {
        const target = index + direction;
        if (target < 0 || target >= next.length) return undefined;
        [next[index], next[target]] = [next[target], next[index]];
        return next;
      }),
    [commitQuestions]
  );

  return (
    <Accordion
      id={`form-block-${block.id}`}
      expanded={expanded}
      onChange={(_, isExpanded) => onToggleExpanded(isExpanded)}
      elevation={0}
      disableGutters
      // Mount content only while open (heavy question editors otherwise lag the
      // expand) and scroll the header to the top once the transition finishes,
      // by which point the previously open block has collapsed.
      slotProps={{
        transition: {
          timeout: BLOCK_TRANSITION_MS,
          mountOnEnter: true,
          unmountOnExit: true,
          onEntered: () =>
            document
              .getElementById(`form-block-${block.id}`)
              ?.scrollIntoView({ behavior: "smooth", block: "start" }),
        },
      }}
      sx={{
        border: "1px solid",
        borderColor: hasBlockErrors ? "error.main" : grey[300],
        borderRadius: "12px",
        scrollMarginTop: "12px",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ "& .MuiAccordionSummary-content": { alignItems: "center" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Typography sx={{ fontWeight: 500, fontSize: 16 }}>
            {block.title?.trim()
              ? block.title
              : t(language, "formBlocks.untitledBlock", "Untitled block")}
          </Typography>
          {hasBlockErrors && (
            <Tooltip
              title={t(
                language,
                "formBlocks.blockHasErrors",
                erroringQuestionCount > 0
                  ? `This block has errors in ${erroringQuestionCount} question(s). Expand to fix them.`
                  : "This block has validation errors. Expand to fix them."
              )}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "error.main",
                }}
              >
                <ErrorOutline fontSize="small" />
                {erroringQuestionCount > 0 && (
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    {erroringQuestionCount}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMoveUp();
            }}
            disabled={isFirst}
          >
            <ArrowUpward fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMoveDown();
            }}
            disabled={isLast}
          >
            <ArrowDownward fontSize="small" />
          </IconButton>
          <FormControlLabel
            labelPlacement="start"
            onClick={(e) => e.stopPropagation()}
            sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
            control={
              <AntSwitch
                checked={block.enabled}
                sx={{ ml: 1 }}
                onChange={(e) =>
                  onChange({ ...block, enabled: e.target.checked })
                }
              />
            }
            label={t(language, "activate", "Activate")}
          />
          <Tooltip
            title={
              questions.length > 0
                ? t(
                    language,
                    "formBlocks.deleteBlockBlocked",
                    "Remove all questions before deleting this block."
                  )
                : ""
            }
          >
            {/* span wrapper so the tooltip still shows while the button is disabled */}
            <span>
              <IconButton
                size="small"
                sx={{ ml: 2 }}
                disabled={questions.length > 0}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <DeleteOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Grid2 container spacing={2}>
          <Grid2 size={6}>
            <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
              {t(language, "formBlocks.blockTitle", "Block title")}
            </Typography>
            <DraftTextField
              value={block.title ?? ""}
              onCommit={(val) => onChange({ ...block, title: val })}
              error={Boolean(validationErrors?.[titleKey])}
              helperText={validationErrors?.[titleKey]?.message}
            />
          </Grid2>
          <Grid2 size={6}>
            <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
              {t(language, "formBlocks.code", "Code")}
            </Typography>
            <DraftTextField
              value={block.code ?? ""}
              onCommit={(val) => onChange({ ...block, code: val })}
              error={Boolean(validationErrors?.[codeKey])}
              helperText={validationErrors?.[codeKey]?.message}
            />
          </Grid2>
          <Grid2 size={12}>
            <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
              {t(language, "formBlocks.blockDescription", "Block description")}
            </Typography>
            <DraftTextField
              value={block.description ?? ""}
              onCommit={(val) => onChange({ ...block, description: val })}
              multiline
            />
          </Grid2>
          <Grid2 size={6}>
            <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
              {t(language, "formBlocks.conditionalOn", "Conditional on")}
            </Typography>
            <ControlledSelectField
              value={block.conditionalOn?.questionId ?? ""}
              onChange={(val) => setControllingQuestion(val as string)}
              options={conditionOptions}
            />
          </Grid2>
          {block.conditionalOn?.questionId && (
            <Grid2 size={6}>
              <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
                {t(language, "formBlocks.conditionEquals", "Equals")}
              </Typography>
              {controllingHasOptions ? (
                <ControlledSelectField
                  multiple
                  value={equalsValues}
                  onChange={(val) =>
                    setEquals(Array.isArray(val) ? val : val ? [val] : [])
                  }
                  options={
                    controllingEnumValues.length > 0
                      ? controllingEnumValues.map((v) => ({
                          value: v,
                          label: v,
                        }))
                      : (controllingQuestion?.options ?? []).map((o) => ({
                          value: o.id,
                          label: o.label?.trim() || o.id,
                        }))
                  }
                />
              ) : (
                <DraftTextField
                  value={equalsValues[0] ?? ""}
                  onCommit={(val) => setEquals(val ? [val] : [])}
                />
              )}
            </Grid2>
          )}
        </Grid2>

        <BlockQuestionList
          questions={questions}
          formIndex={formIndex}
          blockIndex={blockIndex}
          allQuestions={allQuestions}
          validationErrors={validationErrors}
          language={language}
          onChange={updateQuestion}
          onDelete={deleteQuestion}
          onMove={moveQuestion}
        />

        <Box>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={addQuestion}
            sx={{ textTransform: "none", borderRadius: "4px" }}
          >
            {t(language, "formBlocks.addQuestion", "Add Question")}
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
